import React, { useState, useRef, useEffect } from 'react';
import { Dialog, Box, IconButton } from '@mui/material';
import { X, Search, User, Phone, MapPin, Check } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClientOpt } from '../../redux/slices/optionSlice';

/**
 * ClientSelectModal using MUI Dialog
 */
export default function ClientSelectModal({ isOpen, onClose, selectedClient, onSelect }) {
  const dispatch = useDispatch();
  const { clientOpt = [] } = useSelector(state => state.options);
  const [query, setQuery] = useState('');
  const inputRef = useRef();

  useEffect(() => {
    dispatch(fetchClientOpt());
  }, [dispatch]);
  
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [isOpen]);

  const filtered = clientOpt.filter(c => {
    const q = query.toLowerCase();
    return !q
      || (c.client_name || '').toLowerCase().includes(q)
      || (c.phone || '').toLowerCase().includes(q)
      || (c.billing_address || '').toLowerCase().includes(q);
  });

  const handleSelect = (client) => {
    onSelect(client);
    onClose();
  };

  const initials = (name = '') =>
    name.trim().split(/\s+/).map(w => w[0] || '').join('').slice(0, 2).toUpperCase() || '?';

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          style: {
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
            backdropFilter: 'blur(4px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          fontFamily: "'DM Sans', system-ui, sans-serif",
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '85vh',
        }
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
            <User size={15} className="text-white" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-900">Select Client</h2>
            <p className="text-[11px] text-slate-400 mt-0.5">Choose the client for this quotation</p>
          </div>
        </div>
        <IconButton onClick={onClose} size="small" sx={{ color: 'slate.400' }}>
          <X size={16} />
        </IconButton>
      </div>

      {/* Search */}
      <div className="px-5 py-3 border-b border-slate-100">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name, phone or address…"
            className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent placeholder-slate-400"
          />
        </div>
      </div>

      {/* Currently selected banner */}
      {selectedClient && (
        <div className="px-5 py-2.5 bg-emerald-50 border-b border-emerald-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Check size={13} className="text-emerald-600" />
            <span className="text-[11px] font-semibold text-emerald-700">
              Selected: {selectedClient.client_name}
            </span>
          </div>
          <button
            onClick={() => { onSelect(null); onClose(); }}
            className="text-[10px] font-bold text-emerald-600 hover:text-red-500 transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      {/* Client list */}
      <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1.5">
        {clientOpt.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <User size={28} className="mb-2 opacity-30" />
            <p className="text-sm font-medium">Loading clients…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <User size={28} className="mb-2 opacity-30" />
            <p className="text-sm font-medium">No clients found</p>
          </div>
        ) : (
          filtered.map(client => {
            const isSelected = selectedClient?.id === client.id;
            return (
              <button
                key={client.id}
                onClick={() => handleSelect(client)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border text-left transition-all ${
                  isSelected
                    ? 'bg-emerald-50 border-emerald-300'
                    : 'bg-white border-slate-200 hover:border-emerald-200 hover:bg-emerald-50/30'
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                  isSelected ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'
                }`}>
                  {initials(client.client_name)}
                </div>

                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-bold truncate ${isSelected ? 'text-emerald-700' : 'text-slate-800'}`}>
                    {client.client_name}
                  </p>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    {client.phone && (
                      <span className="flex items-center gap-1 text-[10px] text-slate-500 whitespace-nowrap">
                        <Phone size={9} />{client.phone}
                      </span>
                    )}
                    {(client.add_to || client.site_add) && (
                      <span className="flex items-center gap-1 text-[10px] text-slate-400 truncate">
                        <MapPin size={9} /><span className="font-semibold text-slate-500">Billing:</span> {client.add_to || client.site_add}
                      </span>
                    )}
                  </div>
                </div>
                {isSelected && <Check size={15} className="text-emerald-600 flex-shrink-0" />}
              </button>
            );
          })
        )}
      </div>
    </Dialog>
  );
}